import { CommitLogEntry, WizardState } from '../context/types';
import { EntryBadge } from './build-section-summaries';
import { expandCodeRows, flattenCodes } from './expand-codes';
import { formatAddressOneLine } from './format-address';
import { getOrgGroups } from './org-groups';

export type TreeStage = 'review' | 'done';

// Lightweight tree node consumed by ContractTreeView. Mirrors the entity
// ownership graph: Contract (per TIN) holds Customer Account, Merchant +
// Locations + Practitioners, Fee Schedule, and CSPs. Items and Bundle live
// at root level under "Shared resources" because they are not per-TIN.
export interface TreeNode {
  id: string;
  label: string;
  badge?: EntryBadge;
  caption?: string;
  entityId?: string;
  details?: { label: string; value: string }[];
  error?: string;
  defaultExpanded?: boolean;
  children?: TreeNode[];
}

export interface ContractTree {
  // One subtree per distinct TIN (multi-org). Contains everything scoped to
  // a legal entity: customer account, merchant + locations + practitioners,
  // and the contract itself.
  contracts: TreeNode[];
  // Resources shared across the whole input — items, the bundle, and the
  // (single) fee schedule. CSPs hang off each contract.
  shared: TreeNode[];
}

export interface BuildContractTreeArgs {
  state: WizardState;
  stage: TreeStage;
}

const findLog = (
  log: CommitLogEntry[],
  step: string,
): CommitLogEntry | undefined => log.find(l => l.step === step);

const responseId = (
  entry: CommitLogEntry | undefined,
  path: string[],
): string | undefined => {
  if (entry?.response == null) return undefined;
  let cur: unknown = entry.response;
  for (const segment of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[segment];
  }
  return typeof cur === 'string' && cur !== '' ? cur : undefined;
};

const createBadge = (
  stage: TreeStage,
  log: CommitLogEntry[],
  step: string,
): { badge: EntryBadge; error?: string } => {
  if (stage === 'review') return { badge: 'new' };
  const entry = findLog(log, step);
  if (entry == null) return { badge: 'pending' };
  if (entry.status === 'success') return { badge: 'committed' };
  if (entry.status === 'error') return { badge: 'failed', error: entry.error };
  return { badge: 'pending' };
};

const buildCustomerAccountNode = (
  args: BuildContractTreeArgs,
  tin: string,
  isMultiOrg: boolean,
): TreeNode | null => {
  const { state, stage } = args;
  const log = state.commit.log;
  const ca = state.decisions.customerAccount[tin];
  if (ca == null) return null;
  if (ca.mode === 'reuse') {
    return {
      id: `ca:${tin}`,
      label: 'Customer Account',
      caption: `${ca.name} · FEIN ${tin}`,
      badge: 'reuse',
      entityId: ca.id,
      defaultExpanded: false,
    };
  }
  const orgStep = isMultiOrg
    ? `ensureOrganization:${tin}`
    : 'ensureOrganization';
  const paStep = isMultiOrg
    ? `createProviderAccount:${tin}`
    : 'createProviderAccount';
  const orgEntry = findLog(log, orgStep);
  const orgId =
    responseId(orgEntry, ['organization', 'organizationId']) ??
    responseId(orgEntry, ['organization', 'organization_id']);
  const paEntry = findLog(log, paStep);
  const paId =
    responseId(paEntry, ['providerAccount', 'providerAccountId']) ??
    responseId(paEntry, ['providerAccount', 'provider_account_id']);
  const orgInfo = createBadge(stage, log, orgStep);
  const paInfo = createBadge(stage, log, paStep);
  return {
    id: `ca:${tin}`,
    label: 'Customer Account',
    caption: `${ca.name} · FEIN ${ca.taxId}`,
    defaultExpanded: stage === 'review',
    children: [
      {
        id: `ca-org:${tin}`,
        label: `Organization: ${ca.name}`,
        badge: orgInfo.badge,
        entityId: orgId,
        error: orgInfo.error,
      },
      {
        id: `ca-pa:${tin}`,
        label: `ProviderAccount: ${ca.name}`,
        badge: paInfo.badge,
        entityId: paId,
        error: paInfo.error,
      },
    ],
  };
};

const buildLocationNodes = (
  args: BuildContractTreeArgs,
  tin: string,
): TreeNode[] => {
  const { state, stage } = args;
  const input = state.input;
  if (input == null) return [];
  const log = state.commit.log;
  const locs = input.locations.filter(l => l.tax_id === tin);
  const inputSel = state.decisions.locationSelection.input;
  return locs.map(loc => {
    const key = `${loc.address.locality}|${loc.address.address_line_1}`;
    const r = state.resolutions.merchantLocations[key];
    // Unchecked on the Locations step ⇒ orchestrator skips the create+link
    // (see use-commit-orchestrator.ts gate). Show it as excluded, not a create.
    const excluded = inputSel[key] === false;
    let badge: EntryBadge;
    let entityId: string | undefined;
    let error: string | undefined;
    if (excluded) {
      badge = 'skip';
    } else if (r?.status === 'existing') {
      badge = 'reuse';
      entityId = r.data?.merchantLocationId;
    } else {
      // Match the producer's step name (`createMerchantLocation:${key}`), not
      // the locality alone, so done-stage status/id resolve correctly.
      const step = `createMerchantLocation:${key}`;
      const info = createBadge(stage, log, step);
      badge = info.badge;
      error = info.error;
      entityId = responseId(findLog(log, step), ['merchantLocation', 'id']);
    }

    const practitionerNodes: TreeNode[] = loc.practitioners
      .filter(p => p.npi != null)
      .map(p => {
        const npi = p.npi as string;
        const pr = state.resolutions.providerIndividuals[npi];
        if (pr?.status === 'existing') {
          return {
            id: `pi:${loc.address.locality}:${npi}`,
            label: p.name,
            caption: `NPI ${npi}`,
            badge: 'reuse',
            entityId: pr.data?.providerIndividualId,
          } satisfies TreeNode;
        }
        const step = `createProviderIndividual:${npi}`;
        const info = createBadge(stage, log, step);
        const id = responseId(findLog(log, step), ['providerIndividual', 'id']);
        return {
          id: `pi:${loc.address.locality}:${npi}`,
          label: p.name,
          caption: `NPI ${npi}`,
          badge: info.badge,
          entityId: id,
          error: info.error,
        } satisfies TreeNode;
      });

    const noNpiNames = loc.practitioners
      .filter(p => p.npi == null)
      .map(p => p.name);
    if (noNpiNames.length > 0) {
      practitionerNodes.push({
        id: `pi-skipped:${loc.address.locality}`,
        label: `${noNpiNames.length} skipped (no NPI)`,
        caption: noNpiNames.join(', '),
        badge: 'skipped',
      });
    }

    // No AccessPoint nodes under a deselected (skip-badged) location.
    const accessPointNodes: TreeNode[] =
      state.decisions.createAccessPoints && !excluded
        ? loc.practitioners
            .filter(p => p.npi != null)
            .map(p => {
              const npi = p.npi as string;
              const apKey = `${loc.address.locality}|${npi}`;
              const r2 = state.resolutions.accessPoints[apKey];
              if (r2?.status === 'existing') {
                return {
                  id: `ap:${apKey}`,
                  label: `${p.name}`,
                  caption: `NPI ${npi}`,
                  badge: 'reuse',
                  entityId: r2.data?.accessPointId,
                } satisfies TreeNode;
              }
              const step = `createAccessPoint:${loc.address.locality}:${npi}`;
              const info = createBadge(stage, log, step);
              const id = responseId(findLog(log, step), ['accessPoint', 'id']);
              return {
                id: `ap:${apKey}`,
                label: `${p.name}`,
                caption: `NPI ${npi}`,
                badge: info.badge,
                entityId: id,
                error: info.error,
              } satisfies TreeNode;
            })
        : [];

    const children: TreeNode[] = [];
    if (practitionerNodes.length > 0) {
      children.push({
        id: `practitioners:${loc.address.locality}`,
        label: `Practitioners (${practitionerNodes.length})`,
        defaultExpanded: true,
        children: practitionerNodes,
      });
    }
    if (accessPointNodes.length > 0) {
      children.push({
        id: `access-points:${loc.address.locality}`,
        label: `AccessPoints (${accessPointNodes.length})`,
        children: accessPointNodes,
      });
    }

    return {
      id: `loc:${loc.address.locality}`,
      label: loc.address.locality,
      caption: `${formatAddressOneLine(loc.address)} · NPI-2 ${loc.group_npi_type2}`,
      badge,
      entityId,
      error,
      defaultExpanded: false,
      children,
    } satisfies TreeNode;
  });
};

const buildMerchantNode = (
  args: BuildContractTreeArgs,
  tin: string,
  isMultiOrg: boolean,
): TreeNode | null => {
  const { state, stage } = args;
  const input = state.input;
  if (input == null) return null;
  const log = state.commit.log;
  const merchant = state.resolutions.merchant[tin];
  const legalName = state.decisions.orgNames[tin] ?? input.entity_legal_name;
  let badge: EntryBadge;
  let entityId: string | undefined;
  let error: string | undefined;
  if (merchant?.status === 'existing') {
    badge = 'reuse';
    entityId = merchant.data?.merchantId;
  } else if (merchant?.status === 'new') {
    const step = isMultiOrg ? `createMerchant:${tin}` : 'createMerchant';
    const info = createBadge(stage, log, step);
    badge = info.badge;
    error = info.error;
    entityId = responseId(findLog(log, step), ['merchant', 'id']);
  } else {
    return null;
  }
  const locationNodes = buildLocationNodes(args, tin);
  return {
    id: `merchant:${tin}`,
    label: 'Merchant',
    caption: `${legalName} · TIN ${tin}`,
    badge,
    entityId,
    error,
    defaultExpanded: stage === 'review',
    children:
      locationNodes.length > 0
        ? [
            {
              id: `locations:${tin}`,
              label: `Locations (${locationNodes.length})`,
              defaultExpanded: true,
              children: locationNodes,
            },
          ]
        : undefined,
  };
};

const buildContractRootNode = (
  args: BuildContractTreeArgs,
  tin: string,
  isMultiOrg: boolean,
): TreeNode | null => {
  const { state, stage } = args;
  const log = state.commit.log;
  const contract = state.decisions.contract[tin];
  const input = state.input;
  if (input == null) return null;
  const legalName = state.decisions.orgNames[tin] ?? input.entity_legal_name;

  let label: string;
  let caption: string | undefined;
  let badge: EntryBadge | undefined;
  let entityId: string | undefined;
  let error: string | undefined;

  if (contract == null) {
    label = `Contract: ${legalName}`;
    caption = `TIN ${tin} · contract decision pending`;
    badge = 'pending';
  } else if (contract.mode === 'reuse') {
    label = `Contract: ${contract.existingName}`;
    caption = `${contract.effectiveStart} → ${contract.effectiveEnd ?? 'open-ended'} · TIN ${tin}`;
    badge = 'reuse';
    entityId = contract.existingId;
  } else {
    const step = isMultiOrg ? `createContract:${tin}` : 'createContract';
    const info = createBadge(stage, log, step);
    badge = info.badge;
    error = info.error;
    entityId = responseId(findLog(log, step), ['contract', 'id']);
    label = `Contract: ${contract.name}`;
    caption = `${contract.effectiveStart} → ${contract.effectiveEnd ?? 'open-ended'} · TIN ${tin}`;
  }

  const updateStep = isMultiOrg ? `updateContract:${tin}` : 'updateContract';
  const stateEntry = findLog(log, updateStep);
  const stateNote =
    stage === 'review'
      ? 'Will be transitioned to EXECUTED after CSPs are attached.'
      : stateEntry?.status === 'success'
        ? 'EXECUTED'
        : stateEntry?.status === 'error'
          ? `Failed to transition: ${stateEntry.error ?? ''}`
          : 'pending state transition';

  const children: TreeNode[] = [];
  const ca = buildCustomerAccountNode(args, tin, isMultiOrg);
  if (ca != null) children.push(ca);
  const merchant = buildMerchantNode(args, tin, isMultiOrg);
  if (merchant != null) children.push(merchant);

  return {
    id: `contract:${tin}`,
    label,
    caption,
    badge,
    entityId,
    error,
    defaultExpanded: true,
    details: [{ label: 'Final state', value: stateNote }],
    children,
  };
};

const buildItemsNode = (args: BuildContractTreeArgs): TreeNode | null => {
  const { state } = args;
  const input = state.input;
  if (input == null) return null;
  const groupsA = expandCodeRows(input.fee_schedule.codes.category_a);
  const groupsB = expandCodeRows(input.fee_schedule.codes.category_b);
  const allCodes = flattenCodes([...groupsA, ...groupsB]);
  if (allCodes.length === 0) return null;
  const descByCode = new Map<string, string>();
  for (const g of [...groupsA, ...groupsB]) {
    for (const c of g.codes) {
      if (!descByCode.has(c)) descByCode.set(c, g.description);
    }
  }
  const children: TreeNode[] = allCodes.map(code => {
    const r = state.resolutions.items[code];
    const desc = descByCode.get(code) ?? '';
    if (r?.status === 'existing') {
      return {
        id: `item:${code}`,
        label: `CPT ${code}`,
        caption: desc,
        badge: 'reuse',
        entityId: r.data?.itemId,
      } satisfies TreeNode;
    }
    // Codes not found in the KG are informational only — no entities are
    // created during commit (see use-commit-orchestrator.ts).
    return {
      id: `item:${code}`,
      label: `CPT ${code}`,
      caption: desc,
      badge: 'skip',
    } satisfies TreeNode;
  });
  const excludedCount = input.fee_schedule.codes.category_c_excluded.length;
  return {
    id: 'items-root',
    label: `Items & procedure codes (${allCodes.length})`,
    caption: `Cat A: ${input.fee_schedule.codes.category_a.length} · Cat B: ${input.fee_schedule.codes.category_b.length} · Cat C excluded: ${excludedCount}. Existing codes reused; missing codes shown for reference only.`,
    defaultExpanded: false,
    children,
  };
};

const buildFeeScheduleNode = (args: BuildContractTreeArgs): TreeNode | null => {
  const { state, stage } = args;
  const fs = state.decisions.feeSchedule;
  if (fs == null) return null;
  const log = state.commit.log;
  const lineChildren: TreeNode[] = fs.lines.map(l => ({
    id: `fs-line:${l.kind}:${l.refId}`,
    label: l.kind === 'bundle' ? `Bundle ${l.label}` : `CPT ${l.label}`,
    caption: `$${l.priceDollars.toFixed(2)}${l.kind === 'bundle' ? '/case' : ''}`,
  }));
  const dateLine = `${fs.effectiveStart} → ${fs.effectiveEnd}`;
  const stateEntry = findLog(log, 'updateFeeScheduleState');
  const stateNote =
    stage === 'review'
      ? 'Will be transitioned to EXECUTED before linking to contract.'
      : stateEntry?.status === 'success'
        ? 'EXECUTED'
        : stateEntry?.status === 'error'
          ? `Failed to transition: ${stateEntry.error ?? ''}`
          : 'pending state transition';
  if (fs.reuseId != null) {
    return {
      id: 'fee-schedule-root',
      label: `Fee Schedule: ${fs.name}`,
      caption: dateLine,
      badge: 'reuse',
      entityId: fs.reuseId,
      details: [
        { label: 'Lines', value: String(fs.lines.length) },
        { label: 'Final state', value: stateNote },
      ],
      defaultExpanded: false,
      children: lineChildren,
    };
  }
  const info = createBadge(stage, log, 'createFeeSchedule');
  const id = responseId(findLog(log, 'createFeeSchedule'), [
    'feeSchedule',
    'id',
  ]);
  return {
    id: 'fee-schedule-root',
    label: `Fee Schedule: ${fs.name}`,
    caption: dateLine,
    badge: info.badge,
    entityId: id,
    error: info.error,
    details: [
      { label: 'Lines', value: String(fs.lines.length) },
      { label: 'Final state', value: stateNote },
    ],
    defaultExpanded: false,
    children: lineChildren,
  };
};

// buildCspsNode builds the CSP subtree for one contract. In single-org runs
// `tin` is omitted and the un-suffixed step name is used; in multi-org runs
// `tin` scopes both the step name and the row filter to this TIN's locations.
const buildCspsNode = (
  args: BuildContractTreeArgs,
  tin?: string,
  isMultiOrg?: boolean,
): TreeNode | null => {
  const { state, stage } = args;
  const csps = state.decisions.csps;
  if (csps == null) return null;
  const step =
    isMultiOrg && tin != null
      ? `addContractServiceProviders:${tin}`
      : 'addContractServiceProviders';
  // Filter rows to those belonging to this TIN when tin is provided.
  let rows = csps.rows;
  if (tin != null && state.input != null) {
    const tinLocKeys = new Set<string>();
    for (const loc of state.input.locations.filter(l => l.tax_id === tin)) {
      tinLocKeys.add(`${loc.address.locality}|${loc.address.address_line_1}`);
    }
    for (const ml of state.resolutions.existingMerchantLocations[tin] ?? []) {
      if (ml.id != null) tinLocKeys.add(`existing:${ml.id}`);
    }
    rows = rows.filter(r => tinLocKeys.has(r.id));
  }
  const includedRows = rows.filter(r => r.included);
  if (includedRows.length === 0) return null;
  const log = state.commit.log;
  const info = createBadge(stage, log, step);
  const bulkEntry = findLog(log, step);
  const skippedNote =
    bulkEntry?.status === 'success' &&
    typeof bulkEntry.response === 'object' &&
    bulkEntry.response != null &&
    (bulkEntry.response as Record<string, unknown>).alreadyLinked === true
      ? 'All rows already attached server-side; nothing new written.'
      : undefined;
  const children: TreeNode[] = includedRows.map(r => {
    const dateLine = `${r.effectiveStart} → ${r.effectiveEnd ?? 'open-ended'}`;
    let caption: string;
    let resolvedId: string | undefined;
    if (csps.mode === 'access_point') {
      caption = `AccessPoint ${r.id}`;
      resolvedId = state.resolutions.accessPoints[r.id]?.data?.accessPointId;
    } else if (r.id.startsWith('existing:')) {
      // Bottom-table-only row: id suffix is the merchantLocationId itself.
      resolvedId = r.id.slice('existing:'.length);
      caption = `Existing Location ${resolvedId}`;
    } else {
      caption = `Location ${r.id}`;
      resolvedId =
        state.resolutions.merchantLocations[r.id]?.data?.merchantLocationId;
    }
    return {
      id: `csp:${r.id}`,
      label: dateLine,
      caption,
      badge: info.badge,
      entityId: resolvedId,
      error: info.error,
    };
  });
  return {
    id: 'csps-root',
    label: `Service Providers (CSPs) · ${includedRows.length} included`,
    caption:
      `Mode: ${csps.mode}.${skippedNote != null ? ` ${skippedNote}` : ''}`.trim(),
    defaultExpanded: false,
    children,
  };
};

export interface TreeCounts {
  reuse: number;
  new: number;
  committed: number;
  failed: number;
  pending: number;
  skipped: number;
  skip: number;
}

export const countBadges = (nodes: TreeNode[]): TreeCounts => {
  const counts: TreeCounts = {
    reuse: 0,
    new: 0,
    committed: 0,
    failed: 0,
    pending: 0,
    skipped: 0,
    skip: 0,
  };
  const walk = (node: TreeNode): void => {
    if (node.badge != null) counts[node.badge] += 1;
    if (node.children != null) for (const c of node.children) walk(c);
  };
  for (const n of nodes) walk(n);
  return counts;
};

export const buildContractTree = (
  args: BuildContractTreeArgs,
): ContractTree => {
  const { state } = args;
  const input = state.input;
  if (input == null) return { contracts: [], shared: [] };
  const groups = getOrgGroups(input);
  const isMultiOrg = groups.length > 1;
  const contracts: TreeNode[] = [];
  for (const g of groups) {
    const node = buildContractRootNode(args, g.tax_id, isMultiOrg);
    if (node != null) {
      // Fee schedule is shared (single-org: attach under contract; multi-org:
      // surfaces in shared[] below). CSPs are always per-contract — attach
      // under each contract node in both single- and multi-org runs so the
      // tree is accurate about which CSPs belong to which contract.
      if (!isMultiOrg) {
        const fs = buildFeeScheduleNode(args);
        if (fs != null) node.children = [...(node.children ?? []), fs];
      }
      const csps = buildCspsNode(args, g.tax_id, isMultiOrg);
      if (csps != null) node.children = [...(node.children ?? []), csps];
      contracts.push(node);
    }
  }
  const shared: TreeNode[] = [];
  if (isMultiOrg) {
    const fs = buildFeeScheduleNode(args);
    if (fs != null) shared.push(fs);
  }
  const items = buildItemsNode(args);
  if (items != null) shared.push(items);
  return { contracts, shared };
};
